import { redirectToOAuth } from 'v2/pages/settings/integrations/oauth-redirect'
import { useReconnectOrgAppConnectionMutation } from 'v2/pages/settings/integrations/queries'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { getSafeIntegrationErrorMetadata } from '../../utils/safe-error-metadata'

type ReconnectAppCallback = () => void
type ReconnectAppErrorCallback = (error: Error) => void

interface UseReconnectAppReturn {
  reconnectApp: (
    app: RExternalOrgApp,
    onSuccess?: ReconnectAppCallback,
    onError?: ReconnectAppErrorCallback,
  ) => void
  isReconnecting: boolean
}

export const useReconnectApp = (): UseReconnectAppReturn => {
  const { mutate, isPending } = useReconnectOrgAppConnectionMutation()

  const reconnectApp = (
    app: RExternalOrgApp,
    onSuccess?: ReconnectAppCallback,
    onError?: ReconnectAppErrorCallback,
  ) => {
    mutate(
      { appId: app.id },
      {
        onSuccess: (data) => {
          if (!data.oauth_redirect_url) {
            logger.error('Reconnect returned no oauth_redirect_url')
            onError?.(new Error('No redirect URL'))
            return
          }
          onSuccess?.()
          redirectToOAuth(data.oauth_redirect_url)
        },
        onError: (error) => {
          logger.error(
            'Integration reconnect request failed',
            getSafeIntegrationErrorMetadata(error),
          )
          errorToast({
            title: 'Reconnect Failed',
            description: 'Could not initiate reconnection.',
          })
          onError?.(error)
        },
      },
    )
  }

  return {
    reconnectApp,
    isReconnecting: isPending,
  }
}
